// src/lib/dashboard/projecaoFaturamento.ts
// Projeção "se a Vigor Energy não vender mais nada" — ver
// docs/superpowers/specs/2026-08-21-painel-executivo-design.md, seção
// "Projeção de Faturamento". Fórmula simplificada e deliberada (tarifa e
// desconto médios ponderados por kWh, não o cálculo completo de boleto de
// src/lib/calculoBoletoVigor.ts) — não tenta prever bandeira, restituição de
// imposto, energia não isenta etc., só a base recorrente.
export interface CenarioProjecao {
  id: 'A' | 'B' | 'C';
  nome: string;
  churnPct: number; // fração, ex.: 0.05 = 5%
  inadimplenciaPct: number; // fração, ex.: 0.08 = 8%
}

export const CENARIOS_PROJECAO_PADRAO: CenarioProjecao[] = [
  { id: 'A', nome: 'Otimista', churnPct: 0.05, inadimplenciaPct: 0.08 },
  { id: 'B', nome: 'Conservador', churnPct: 0.10, inadimplenciaPct: 0.15 },
  { id: 'C', nome: 'Pessimista', churnPct: 0.15, inadimplenciaPct: 0.20 },
];

export interface BaseProjecao {
  kwhBase: number; // soma de energia_alvo_kwh do último rateio conciliado por UG
  tarifaMedia: number; // R$/kWh, média ponderada por kWh entre as UCs da base
  descontoMedio: number; // fração, média ponderada por kWh
}

export interface ResultadoProjecaoCenario {
  cenario: CenarioProjecao;
  kwhProjetado: number;
  emitidoMensal: number;
  recebidoMensal: number;
  emitidoTotalHorizonte: number;
  recebidoTotalHorizonte: number;
}

/**
 * Base recorrente a partir da previsão REAL por UC.
 *
 * A base antiga (`BaseProjecao`: kWh contratado × tarifa média × desconto
 * médio) não conseguia bater com a realidade por construção, porque a Vigor
 * fatura a energia INJETADA pelo rateio, não o consumo contratado da UC.
 * Medido em 29/08/2026: aquela conta dava R$ 951 mil/mês contra R$ 610-644
 * mil/mês realmente emitidos.
 *
 * Aqui a base é o próprio "a faturar" mensal somado UC a UC — a mesma fonte
 * do card "Quanto vou receber". Os cenários continuam existindo e continuam
 * respondendo a pergunta que só eles respondem: o que acontece com essa base
 * ao longo de N meses se ninguém vender mais nada e uma fatia cancelar.
 *
 * Usa a MEDIANA dos meses projetados, não a média: o primeiro e o último mês
 * do horizonte são parciais (o mês corrente já venceu quase todo, o último só
 * tem parte das UCs projetadas) e puxariam a média para baixo.
 */
export function baseRecorrenteMensal(
  mesesAFaturar: readonly number[],
): { valorMensal: number; mesesConsiderados: number } {
  const cheios = mesesAFaturar.filter((v) => v > 0).sort((a, b) => a - b);
  if (cheios.length === 0) return { valorMensal: 0, mesesConsiderados: 0 };
  const meio = Math.floor(cheios.length / 2);
  const mediana = cheios.length % 2 === 1 ? cheios[meio] : (cheios[meio - 1] + cheios[meio]) / 2;
  return { valorMensal: mediana, mesesConsiderados: cheios.length };
}

/**
 * Cenário sobre uma base já em reais.
 *
 * Mesma regra de `calcularProjecaoCenario` — churn corta a base uma única vez,
 * inadimplência reduz só o Recebido — mas partindo do valor faturado real em
 * vez de reconstruí-lo de kWh × tarifa.
 */
export function calcularProjecaoDeBaseReal(
  valorMensal: number,
  cenario: CenarioProjecao,
  horizonteMeses: number,
): ResultadoProjecaoCenario {
  const emitidoMensal = valorMensal * (1 - cenario.churnPct);
  const recebidoMensal = emitidoMensal * (1 - cenario.inadimplenciaPct);
  return {
    cenario,
    kwhProjetado: 0,
    emitidoMensal,
    recebidoMensal,
    emitidoTotalHorizonte: emitidoMensal * horizonteMeses,
    recebidoTotalHorizonte: recebidoMensal * horizonteMeses,
  };
}

export function calcularMediaPonderada(itens: { peso: number; valor: number }[]): number {
  const pesoTotal = itens.reduce((s, i) => s + i.peso, 0);
  if (pesoTotal <= 0) return 0;
  return itens.reduce((s, i) => s + i.peso * i.valor, 0) / pesoTotal;
}

// churn é um corte ÚNICO sobre a base (não composto mês a mês) — o valor
// mensal projetado é o mesmo em todos os meses do horizonte. Inadimplência
// reduz só o Recebido, nunca o Emitido.
export function calcularProjecaoCenario(base: BaseProjecao, cenario: CenarioProjecao, horizonteMeses: number): ResultadoProjecaoCenario {
  const kwhProjetado = base.kwhBase * (1 - cenario.churnPct);
  const emitidoMensal = base.tarifaMedia * (1 - base.descontoMedio) * kwhProjetado;
  const recebidoMensal = emitidoMensal * (1 - cenario.inadimplenciaPct);
  return {
    cenario,
    kwhProjetado,
    emitidoMensal,
    recebidoMensal,
    emitidoTotalHorizonte: emitidoMensal * horizonteMeses,
    recebidoTotalHorizonte: recebidoMensal * horizonteMeses,
  };
}

export function calcularProjecaoTodosCenarios(base: BaseProjecao, cenarios: CenarioProjecao[], horizonteMeses: number): ResultadoProjecaoCenario[] {
  return cenarios.map((c) => calcularProjecaoCenario(base, c, horizonteMeses));
}
